import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ChartBar, UploadSimple, TreeStructure, GitBranch, ClockCounterClockwise,
  CheckCircle, WarningCircle, XCircle, ArrowClockwise, ShieldWarning, Ruler, Camera, FileText, DownloadSimple,
  Gauge, SealCheck, Recycle, Info, Lightbulb, Graph, Wrench, Clock, GraduationCap,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const LIB_LABEL = {
  catalogs: "Kataloge", pruefgruppen: "Prüfgruppen", checkpoints: "Prüfpunkte",
  komponenten: "Komponenten", maengel: "Mängel", reparatur_pakete: "Reparaturpakete",
  business_rules: "Business Rules", normen: "Normverweise", health_rules: "Health-Regeln",
};
const PRIO_TONE = { niedrig: "green", mittel: "blue", hoch: "amber", kritisch: "red" };

const fmtDate = (s) => (s ? new Date(s).toLocaleString("de-DE") : "—");

// ------------------------------------------------------------------ 1. Diagnose
function DiagnoseTab() {
  const [diag, setDiag] = useState(null);
  useEffect(() => { api.get("/v1/cms/diagnostics").then((r) => setDiag(r.data)).catch(() => setDiag(false)); }, []);
  if (diag === null) return <Skeleton className="h-40 rounded-md" />;
  if (diag === false) return <p className="text-sm text-muted-foreground">Diagnose konnte nicht geladen werden.</p>;
  const order = ["catalogs", "pruefgruppen", "checkpoints", "komponenten", "maengel", "reparatur_pakete", "business_rules", "normen", "health_rules"];
  return (
    <div className="space-y-6" data-testid="cms-diagnose">
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-3">
        {order.map((k) => (
          <Card key={k} data-testid={`diag-count-${k}`} className="rounded-md border p-4">
            <div className="text-4xl font-black tabular-nums tracking-tighter">{diag.counts[k] ?? 0}</div>
            <div className="mt-1"><Label2>{LIB_LABEL[k]}</Label2></div>
          </Card>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="rounded-md border p-4"><Label2>Importvorgänge</Label2><div className="mt-1 text-2xl font-bold tabular-nums">{diag.import_count}</div></Card>
        <Card className="rounded-md border p-4"><Label2>Pakete</Label2><div className="mt-1 flex flex-wrap gap-1">{(diag.pakete || []).length ? diag.pakete.map((p) => <Badge key={p} variant="outline" className="rounded-md">{p}</Badge>) : <span className="text-sm text-muted-foreground">—</span>}</div></Card>
        <Card className="rounded-md border p-4"><Label2>Letzter Import</Label2><div className="mt-1 text-sm">{diag.last_import ? <>{diag.last_import.paket} v{diag.last_import.version} · {fmtDate(diag.last_import.ts)} {diag.last_import.dry_run ? <Badge variant="outline" className="ml-1 rounded-md">Dry-Run</Badge> : null}</> : "—"}</div></Card>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ 2. Paketimport
function ImportTab({ onImported }) {
  const [pkg, setPkg] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [isZip, setIsZip] = useState(false);
  const [fileName, setFileName] = useState("");
  const [parseErr, setParseErr] = useState("");
  const [dry, setDry] = useState(null);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef();

  const reset = () => { setPkg(null); setZipFile(null); setIsZip(false); setFileName(""); setParseErr(""); setDry(null); setErrors([]); setDone(false); };

  const onFile = (e) => {
    reset();
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    if (f.name.toLowerCase().endsWith(".zip")) {
      setIsZip(true); setZipFile(f);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const obj = JSON.parse(reader.result);
        if (!obj.cms_package) { setParseErr("Feld 'cms_package' fehlt — kein gültiges CMS-Paket (JSON)."); return; }
        setPkg(obj);
      } catch (err) { setParseErr("Ungültiges JSON: " + err.message); }
    };
    reader.readAsText(f);
  };

  const ready = pkg || zipFile;

  const run = async (dryRun) => {
    if (!ready) return;
    setBusy(true); setErrors([]); if (dryRun) setDry(null);
    try {
      let data;
      if (isZip) {
        const fd = new FormData();
        fd.append("file", zipFile);
        ({ data } = await api.post(`/v1/cms/import-zip?dry_run=${dryRun}`, fd));
      } else {
        ({ data } = await api.post(`/v1/cms/import-package?dry_run=${dryRun}`, pkg));
      }
      setDry(data.summary);
      if (!dryRun) { setDone(true); toast.success("Import durchgeführt", { description: `${data.package} v${data.version}` }); onImported?.(); }
      else toast.success("Dry-Run erfolgreich — keine Daten geschrieben");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && Array.isArray(d.errors)) setErrors(d.errors);
      else setErrors([formatApiError(d)]);
      toast.error("Validierung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const summaryRows = dry ? Object.entries(dry).filter(([k]) => k !== "catalog") : [];

  return (
    <div className="space-y-4" data-testid="cms-import">
      <Card className="rounded-md border p-4">
        <Label2>CMS-Paket auswählen (JSON-Paket oder ZIP-Export)</Label2>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="application/json,.json,.zip,application/zip" onChange={onFile} className="hidden" data-testid="cms-file-input" />
          <Button variant="outline" size="sm" className="gap-2" onClick={() => fileRef.current?.click()} data-testid="cms-file-btn"><UploadSimple size={16} /> Datei wählen</Button>
          {fileName && <span className="text-sm text-muted-foreground">{fileName}</span>}
          {isZip && zipFile && <Badge variant="outline" className="rounded-md">ZIP-Paket</Badge>}
          {pkg && <Badge variant="outline" className="rounded-md">{pkg.cms_package} v{pkg.version || "?"}</Badge>}
        </div>
        {parseErr && <p className="mt-2 text-sm text-danger" data-testid="cms-parse-error">{parseErr}</p>}
        {ready && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => run(true)} data-testid="cms-dryrun-btn">Dry-Run starten</Button>
            <Button size="sm" disabled={busy || errors.length > 0 || done} onClick={() => run(false)} data-testid="cms-import-btn">Import durchführen</Button>
            {done && <StatusBadge tone="green" testid="cms-import-done">Importiert</StatusBadge>}
          </div>
        )}
      </Card>

      {errors.length > 0 && (
        <Card className="rounded-md border border-danger/40 bg-danger/5 p-4" data-testid="cms-errors">
          <div className="mb-2 flex items-center gap-2 text-danger"><XCircle size={18} weight="fill" /><span className="font-semibold">Validierungs-/Referenzfehler ({errors.length})</span></div>
          <ul className="space-y-1 text-sm">{errors.map((er, i) => <li key={i} className="font-mono text-xs text-danger">• {er}</li>)}</ul>
        </Card>
      )}

      {dry && (
        <Card className="rounded-md border p-4" data-testid="cms-summary">
          <div className="mb-2 flex items-center gap-2"><CheckCircle size={18} weight="fill" className="text-success" /><span className="font-semibold">Zusammenfassung der Änderungen</span></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-4 font-medium">Bibliothek</th><th className="px-2 font-medium">Neu</th><th className="px-2 font-medium">Aktualisiert</th><th className="px-2 font-medium">Gesamt</th></tr></thead>
              <tbody>
                {summaryRows.map(([lib, s]) => (
                  <tr key={lib} className="border-t border-border/60"><td className="py-1 pr-4">{LIB_LABEL[lib] || lib}</td><td className="px-2 tabular-nums text-success">{s.created}</td><td className="px-2 tabular-nums text-primary">{s.updated}</td><td className="px-2 tabular-nums">{s.total}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {dry.catalog && <div className="mt-3 text-sm"><Label2>Katalog</Label2> <span className="ml-2">{dry.catalog.key} → <b>{dry.catalog.action === "create" ? "anlegen" : "aktualisieren"}</b> · {dry.catalog.gruppen} Gruppen · {dry.catalog.checkpoints} Prüfpunkte</span></div>}
        </Card>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ 3. Katalog-Inspektion
const KLASSE_TONE = { geringfuegig: "text-success", erheblich: "text-warning", gefaehrlich: "text-danger" };

function RefRow({ label, items, icon: Icon }) {
  if (!items?.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      {items.map((x) => (
        <Badge key={x.key} variant="outline" className="rounded-md gap-1 text-[11px]">{Icon && <Icon size={11} />}{x.bezeichnung || x.name || x.key}</Badge>
      ))}
    </div>
  );
}

function MangelRow({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Mögliche Mängel</span>
      {items.map((m) => (
        <Badge key={m.key} variant="outline" className="rounded-md gap-1 text-[11px]" data-testid={`cms-mangel-${m.key}`}>
          <WarningCircle size={11} className={KLASSE_TONE[m.mangelklasse] || ""} />
          {m.bezeichnung || m.key}
          {m.mangelklasse && <span className={`ml-1 text-[9px] uppercase ${KLASSE_TONE[m.mangelklasse] || ""}`}>{m.mangelklasse}</span>}
          {m.prioritaet && <span className="ml-0.5 text-[9px] uppercase text-muted-foreground">· {m.prioritaet}</span>}
          {m.sicherheitsrelevant && <ShieldWarning size={10} className="text-danger" />}
        </Badge>
      ))}
    </div>
  );
}

function InspektionTab() {
  const [cats, setCats] = useState(null);
  const [sel, setSel] = useState("");
  const [resolved, setResolved] = useState(null);
  const [health, setHealth] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/v1/cms/catalogs").then((r) => { setCats(r.data); if (r.data[0]) setSel(r.data[0].key); });
    api.get("/v1/cms/health_rules").then((r) => setHealth(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!sel) return;
    setLoading(true);
    api.get(`/v1/cms/catalogs/${encodeURIComponent(sel)}`).then((r) => setResolved(r.data)).finally(() => setLoading(false));
  }, [sel]);

  if (cats === null) return <Skeleton className="h-40 rounded-md" />;
  if (!cats.length) return <p className="text-sm text-muted-foreground">Kein Katalog vorhanden. Bitte zuerst ein CMS-Paket importieren.</p>;

  return (
    <div className="space-y-4" data-testid="cms-inspektion">
      <div className="flex flex-wrap items-center gap-3">
        <Label2>Katalog</Label2>
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger className="w-72" data-testid="cms-catalog-select"><SelectValue /></SelectTrigger>
          <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.name} ({c.key} v{c.cms_version})</SelectItem>)}</SelectContent>
        </Select>
        {sel && resolved && (
          <Button size="sm" variant="outline" className="gap-2" data-testid="cms-export-btn"
            onClick={() => downloadFile(`/v1/cms/catalogs/${encodeURIComponent(sel)}/export`, `${sel}_v${resolved.catalog.cms_version}.zip`)
              .then(() => toast.success("Export erstellt", { description: `${sel} v${resolved.catalog.cms_version}` }))
              .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))}>
            <DownloadSimple size={16} /> Katalog exportieren (ZIP)
          </Button>
        )}
      </div>

      {loading || !resolved ? <Skeleton className="h-64 rounded-md" /> : (
        <>
          <Card className="rounded-md border p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><div className="font-heading text-lg font-black">{resolved.catalog.name}</div><div className="text-xs text-muted-foreground">{resolved.catalog.anlagentyp_key} · v{resolved.catalog.cms_version} · {resolved.catalog.sprache}</div></div>
              <div className="flex gap-2"><StatusBadge tone="blue">{resolved.gruppen.length} Prüfgruppen</StatusBadge><StatusBadge tone="blue">{resolved.total_checkpoints} Prüfpunkte</StatusBadge></div>
            </div>
          </Card>

          {resolved.gruppen.map((g, gi) => (
            <Card key={gi} className="rounded-md border p-4" data-testid={`cms-group-${g.pruefgruppe.key}`}>
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded bg-primary/10 text-xs font-bold text-primary">{gi + 1}</span>
                <span className="font-heading font-bold">{g.pruefgruppe.name || g.pruefgruppe.key}</span>
                <span className="text-xs text-muted-foreground">({g.checkpoints.length})</span>
              </div>
              <div className="space-y-3">
                {g.checkpoints.map((cp) => (
                  <div key={cp.key} className="rounded-md border border-border/60 p-3" data-testid={`cms-cp-${cp.key}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold">{cp.bezeichnung}</span>
                      {cp.pflicht && <Badge variant="outline" className="rounded-md text-[10px]">Pflicht</Badge>}
                      {cp.sicherheitsrelevant && <Badge variant="outline" className="rounded-md gap-1 border-danger/40 text-[10px] text-danger"><ShieldWarning size={11} />Sicherheit</Badge>}
                      {cp.foto_pflicht && <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground"><Camera size={13} />Fotopflicht</span>}
                      {cp.messwert_pflicht && <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground"><Ruler size={13} />Messwert</span>}
                      {cp.prioritaet && <span className="text-[10px] uppercase text-muted-foreground">Prio: {cp.prioritaet}</span>}
                      {cp.health_gewicht != null && <span className="text-[10px] text-muted-foreground">Health −{cp.health_gewicht}</span>}
                      <span className="ml-auto font-mono text-[10px] text-muted-foreground">{cp.key}</span>
                    </div>
                    {cp.beschreibung && <p className="mt-1 text-xs">{cp.beschreibung}</p>}
                    {cp.pruefanweisung && <p className="mt-0.5 text-xs italic text-muted-foreground">Anweisung: {cp.pruefanweisung}</p>}
                    {cp.messwert && (cp.messwert.einheit || cp.messwert.quelle) && (
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        Messgröße: {cp.messwert.einheit || "—"}
                        {cp.messwert.sollbereich ? ` · Soll ${cp.messwert.sollbereich}` : ""}
                        {cp.messwert.grenzwert ? ` · Grenzwert ${cp.messwert.grenzwert}` : ""}
                        {cp.messwert.quelle ? ` · Quelle: ${cp.messwert.quelle}` : ""}
                      </div>
                    )}
                    <RefRow label="Komponenten" items={cp.komponenten_ref} icon={TreeStructure} />
                    <MangelRow items={cp.maengel_ref} />
                    <RefRow label="Reparaturpakete" items={cp.reparatur_pakete_ref} icon={ArrowClockwise} />
                    <RefRow label="Business Rules" items={cp.business_rules_ref} icon={GitBranch} />
                    <RefRow label="Normen" items={cp.normen_ref} icon={FileText} />
                    {cp.best_practice && (
                      <p className="mt-2 rounded-md bg-primary/5 px-2 py-1 text-[11px] text-primary" data-testid={`cms-bp-${cp.key}`}>
                        💡 Celentano Best Practice: {cp.best_practice}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}

          {health.length > 0 && (
            <Card className="rounded-md border p-4" data-testid="cms-health-rules">
              <div className="mb-2 font-heading font-bold">Health-Regeln</div>
              {health.map((h) => <div key={h.key} className="text-sm"><span className="font-mono text-xs">{h.key}</span> — {h.beschreibung} {h.defaults && <span className="text-muted-foreground">({Object.entries(h.defaults).map(([k, v]) => `${k}: −${v}`).join(", ")})</span>}</div>)}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ 4. Referenzprüfung
function Section({ title, count, tone, children }) {
  return (
    <Card className="rounded-md border p-4">
      <div className="mb-2 flex items-center gap-2">
        {count === 0 ? <CheckCircle size={18} weight="fill" className="text-success" /> : <WarningCircle size={18} weight="fill" className="text-warning" />}
        <span className="font-semibold">{title}</span>
        <Badge variant="outline" className="rounded-md">{count}</Badge>
      </div>
      {count === 0 ? <p className="text-sm text-muted-foreground">Keine Auffälligkeiten.</p> : children}
    </Card>
  );
}

function ReferenzTab() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/v1/cms/analysis").then((r) => setA(r.data)).catch(() => setA(false)); }, []);
  if (a === null) return <Skeleton className="h-40 rounded-md" />;
  if (a === false) return <p className="text-sm text-muted-foreground">Analyse konnte nicht geladen werden.</p>;
  const unusedTotal = Object.values(a.unused).reduce((n, l) => n + l.length, 0);
  const dupTotal = Object.values(a.duplicate_keys).reduce((n, l) => n + l.length, 0);
  return (
    <div className="space-y-4" data-testid="cms-referenz">
      {a.ok && <Card className="rounded-md border border-success/40 bg-success/5 p-4"><div className="flex items-center gap-2 text-success"><CheckCircle size={20} weight="fill" /><span className="font-semibold">Referenzintegrität vollständig — keine Probleme gefunden.</span></div></Card>}
      <Section title="Fehlende Referenzen" count={a.missing_references.length} tone="red">
        <ul className="space-y-1 text-sm">{a.missing_references.map((m, i) => <li key={i} className="font-mono text-xs text-danger">• {m.from} → {m.field}: {m.target}-Key „{m.key}" fehlt</li>)}</ul>
      </Section>
      <Section title="Ungenutzte Einträge" count={unusedTotal}>
        <div className="space-y-1 text-sm">{Object.entries(a.unused).filter(([, l]) => l.length).map(([lib, l]) => <div key={lib}><Label2>{LIB_LABEL[lib]}</Label2> <span className="ml-2 font-mono text-xs text-muted-foreground">{l.join(", ")}</span></div>)}</div>
      </Section>
      <Section title="Doppelte IDs" count={dupTotal}>
        <div className="space-y-1 text-sm">{Object.entries(a.duplicate_keys).map(([lib, l]) => <div key={lib}><Label2>{LIB_LABEL[lib]}</Label2> {l.map((d) => <span key={d.key} className="ml-2 font-mono text-xs text-danger">{d.key} ×{d.count}</span>)}</div>)}</div>
      </Section>
      <Section title="Kreisreferenzen" count={a.circular_references.length}>
        <p className="mb-2 text-xs text-muted-foreground">Hinweis: Verweise zwischen Komponente ↔ Reparaturpaket ↔ Prüfpunkt sind im Fachmodell wechselseitig und bilden erwartete Zyklen — sie sind keine Fehler, sondern zeigen die Wartungslogik (Mangel → Reparatur → Nachprüfung). Die Auflösung erfolgt einstufig, daher keine Endlosschleife.</p>
        <ul className="space-y-1 text-sm">{a.circular_references.map((c, i) => <li key={i} className="font-mono text-xs text-muted-foreground">• {c.join(" → ")}</li>)}</ul>
      </Section>
      <Section title="Versionskonflikte" count={a.version_conflicts.length}>
        <ul className="space-y-1 text-sm">{a.version_conflicts.map((v, i) => <li key={i} className="text-sm">{v.catalog}: Versionen {v.versions.join(", ")}</li>)}</ul>
      </Section>
    </div>
  );
}

// ------------------------------------------------------------------ 5. Audit
function AuditTab() {
  const [log, setLog] = useState(null);
  useEffect(() => { api.get("/v1/cms/import-log?limit=100").then((r) => setLog(r.data)).catch(() => setLog(false)); }, []);
  if (log === null) return <Skeleton className="h-40 rounded-md" />;
  if (log === false) return <p className="text-sm text-muted-foreground">Import-Log konnte nicht geladen werden.</p>;
  return (
    <div className="space-y-4" data-testid="cms-audit">
      <Card className="rounded-md border border-primary/30 bg-primary/5 p-4">
        <div className="flex items-start gap-2 text-sm"><ClockCounterClockwise size={18} className="mt-0.5 text-primary" /><div>
          <span className="font-semibold">Rollback-Modell</span>
          <p className="mt-1 text-muted-foreground">Jeder Import wird zunächst vollständig validiert und erst dann geschrieben. Bei einem Fehler während des Schreibens rollt die Engine automatisch komplett zurück (Snapshots für Updates, Delete für Inserts) — kein Teilzustand. Kataloge werden versioniert gespeichert, sodass mehrere Paket-Versionen koexistieren und ältere Versionen jederzeit auflösbar bleiben.</p>
        </div></div>
      </Card>
      <Card className="rounded-md border p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground"><tr><th className="p-3 font-medium">Zeitpunkt</th><th className="p-3 font-medium">Typ</th><th className="p-3 font-medium">Paket</th><th className="p-3 font-medium">Version</th><th className="p-3 font-medium">Modus</th><th className="p-3 font-medium">Ergebnis</th><th className="p-3 font-medium">Benutzer</th></tr></thead>
            <tbody>
              {log.map((l) => (
                <tr key={l.id} className="border-t border-border/60" data-testid="cms-log-row">
                  <td className="p-3 whitespace-nowrap">{fmtDate(l.ts)}</td>
                  <td className="p-3">{l.type}</td>
                  <td className="p-3">{l.paket}</td>
                  <td className="p-3 tabular-nums">{l.version}</td>
                  <td className="p-3">{l.dry_run ? <Badge variant="outline" className="rounded-md">Dry-Run</Badge> : <Badge variant="outline" className="rounded-md">Import</Badge>}</td>
                  <td className="p-3">{l.ok ? <StatusBadge tone="green">OK</StatusBadge> : <StatusBadge tone="red">{(l.errors || []).length} Fehler</StatusBadge>}</td>
                  <td className="p-3">{l.user}</td>
                </tr>
              ))}
              {!log.length && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Noch keine Importvorgänge.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ 6. CMS-Core (Wissensgraph)
const KLASSE_TONE2 = { geringfuegig: "text-success", erheblich: "text-warning", gefaehrlich: "text-danger" };

function Chips({ label, keys, map, icon: Icon, tone }) {
  const arr = (keys || []).filter(Boolean);
  if (!arr.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      {arr.map((k) => (
        <Badge key={k} variant="outline" className={`rounded-md gap-1 text-[11px] ${tone || ""}`}>{Icon && <Icon size={11} />}{(map && map[k]) || k}</Badge>
      ))}
    </div>
  );
}

function CoreTab() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("komponenten");

  useEffect(() => {
    Promise.all(["komponenten", "maengel", "reparatur_pakete", "business_rules", "health_rules", "normen"]
      .map((l) => api.get(`/v1/cms/${l}`).then((r) => [l, r.data])))
      .then((pairs) => setData(Object.fromEntries(pairs)))
      .catch(() => setData(false));
  }, []);

  if (data === null) return <Skeleton className="h-40 rounded-md" />;
  if (data === false) return <p className="text-sm text-muted-foreground">CMS-Core konnte nicht geladen werden.</p>;

  const nm = (lib) => Object.fromEntries((data[lib] || []).map((x) => [x.key, x.bezeichnung || x.name || x.key]));
  const maps = { komponenten: nm("komponenten"), maengel: nm("maengel"), reparatur_pakete: nm("reparatur_pakete"), business_rules: nm("business_rules"), normen: nm("normen") };

  const VIEWS = [
    ["komponenten", "Komponenten", TreeStructure],
    ["maengel", "Mängel", WarningCircle],
    ["reparatur_pakete", "Reparaturpakete", ArrowClockwise],
    ["business_rules", "Business Rules", GitBranch],
    ["health_rules", "Health-Regeln", Gauge],
    ["normen", "Normen", FileText],
  ];

  return (
    <div className="space-y-4" data-testid="cms-core">
      <Card className="rounded-md border border-primary/30 bg-primary/5 p-4">
        <div className="flex items-start gap-2 text-sm"><Graph size={18} className="mt-0.5 text-primary" /><div>
          <span className="font-semibold">CMS-Core · Wissensgraph (library = core)</span>
          <p className="mt-1 text-muted-foreground">Katalogübergreifende Single Source of Truth. Alle Objekte sind untereinander vernetzt (Komponente ↔ Mangel ↔ Reparatur ↔ Business Rule ↔ Norm ↔ Prüfpunkt) und werden von allen CMS-Katalogen referenziert. Neue Kataloge bestehen überwiegend aus Referenzen auf diese Bibliotheken.</p>
        </div></div>
      </Card>

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {VIEWS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`core-view-${k}`}
            className={`flex flex-col items-center gap-1 rounded-md border p-3 transition-colors ${view === k ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted/50"}`}>
            <Icon size={18} />
            <span className="text-2xl font-black tabular-nums leading-none">{(data[k] || []).length}</span>
            <span className="text-[10px] uppercase text-muted-foreground">{label}</span>
          </button>
        ))}
      </div>

      <div className="space-y-2" data-testid={`core-list-${view}`}>
        {view === "komponenten" && data.komponenten.map((c) => (
          <Card key={c.key} className="rounded-md border p-3" data-testid={`core-komp-${c.key}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">{c.bezeichnung}</span>
              {c.typ && <Badge variant="outline" className="rounded-md text-[10px]">{c.typ}</Badge>}
              {c.sicherheitsrelevant && <Badge variant="outline" className="rounded-md gap-1 border-danger/40 text-[10px] text-danger"><ShieldWarning size={11} />Sicherheit</Badge>}
              {c.wartungsintervall_monate != null && <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground"><Clock size={12} />{c.wartungsintervall_monate} Mon.</span>}
              {c.lebensdauer_zyklen != null && <span className="text-[10px] text-muted-foreground">{c.lebensdauer_zyklen.toLocaleString("de-DE")} Zyklen</span>}
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">{c.key}</span>
            </div>
            {c.beschreibung && <p className="mt-1 text-xs text-muted-foreground">{c.beschreibung}</p>}
            <Chips label="Typische Mängel" keys={c.maengel} map={maps.maengel} icon={WarningCircle} />
            <Chips label="Reparaturpakete" keys={c.reparatur_pakete} map={maps.reparatur_pakete} icon={ArrowClockwise} />
            <Chips label="Normen" keys={c.normen} map={maps.normen} icon={FileText} />
          </Card>
        ))}

        {view === "maengel" && data.maengel.map((m) => (
          <Card key={m.key} className="rounded-md border p-3" data-testid={`core-mangel-${m.key}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">{m.bezeichnung}</span>
              {m.mangelklasse && <span className={`text-[10px] uppercase ${KLASSE_TONE2[m.mangelklasse] || ""}`}>{m.mangelklasse}</span>}
              {m.prioritaet && <span className="text-[10px] uppercase text-muted-foreground">· {m.prioritaet}</span>}
              {m.sicherheitsrelevant && <ShieldWarning size={12} className="text-danger" />}
              {m.health_auswirkung != null && <span className="text-[10px] text-muted-foreground">Health −{m.health_auswirkung}</span>}
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">{m.key}</span>
            </div>
            {m.beschreibung && <p className="mt-1 text-xs text-muted-foreground">{m.beschreibung}</p>}
            <Chips label="Reparaturpakete" keys={m.reparatur_pakete} map={maps.reparatur_pakete} icon={ArrowClockwise} />
            <Chips label="Folgeprüfungen" keys={m.folgepruefungen} map={{}} icon={CheckCircle} />
            <Chips label="Business Rules" keys={m.business_rules} map={maps.business_rules} icon={GitBranch} />
            <Chips label="Normen" keys={m.normen} map={maps.normen} icon={FileText} />
          </Card>
        ))}

        {view === "reparatur_pakete" && data.reparatur_pakete.map((r) => (
          <Card key={r.key} className="rounded-md border p-3" data-testid={`core-rep-${r.key}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">{r.name}</span>
              {r.sicherheitsrelevant && <Badge variant="outline" className="rounded-md gap-1 border-danger/40 text-[10px] text-danger"><ShieldWarning size={11} />Sicherheit</Badge>}
              {r.arbeitszeit != null && <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground"><Clock size={12} />{r.arbeitszeit} min</span>}
              {r.nachpruefung_erforderlich && <Badge variant="outline" className="rounded-md text-[10px]">Nachprüfung</Badge>}
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">{r.key}</span>
            </div>
            {r.beschreibung && <p className="mt-1 text-xs text-muted-foreground">{r.beschreibung}</p>}
            {r.qualifikation && <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground"><GraduationCap size={12} />{r.qualifikation}</div>}
            <Chips label="Betroffene Komponenten" keys={r.komponenten} map={maps.komponenten} icon={TreeStructure} />
            <Chips label="Business Rules" keys={r.business_rules} map={maps.business_rules} icon={GitBranch} />
          </Card>
        ))}

        {view === "business_rules" && data.business_rules.map((b) => (
          <Card key={b.key} className="rounded-md border p-3" data-testid={`core-br-${b.key}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">{b.name}</span>
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">{b.key}</span>
            </div>
            {b.trigger && <p className="mt-1 text-xs text-muted-foreground">Auslöser: {b.trigger.typ}{b.trigger.wert ? ` = ${b.trigger.wert}` : ""}{b.trigger.mangel_key ? ` (${maps.maengel[b.trigger.mangel_key] || b.trigger.mangel_key})` : ""}</p>}
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Aktionen</span>
              {(b.aktionen || []).map((a, i) => <Badge key={i} variant="outline" className="rounded-md text-[11px]">{a.typ}{a.wert ? `: ${a.wert}` : ""}{a.key ? `: ${maps.reparatur_pakete[a.key] || a.key}` : ""}{a.checkpoint ? `: ${a.checkpoint}` : ""}</Badge>)}
            </div>
          </Card>
        ))}

        {view === "health_rules" && data.health_rules.map((h) => (
          <Card key={h.key} className="rounded-md border p-3" data-testid={`core-hr-${h.key}`}>
            <div className="text-sm font-semibold">{h.beschreibung || h.key}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {h.defaults && <span>Standard: {Object.entries(h.defaults).map(([k, v]) => `${k} −${v}`).join(" · ")}</span>}
              {h.faktoren && <span>Faktoren: {Object.entries(h.faktoren).map(([k, v]) => `${k} ×${v}`).join(" · ")}</span>}
            </div>
          </Card>
        ))}

        {view === "normen" && data.normen.map((n) => (
          <Card key={n.key} className="rounded-md border p-3" data-testid={`core-norm-${n.key}`}>
            <div className="flex items-center gap-2"><FileText size={14} className="text-muted-foreground" /><span className="text-sm font-semibold">{n.bezeichnung}</span><span className="ml-auto font-mono text-[10px] text-muted-foreground">{n.key}</span></div>
            {n.beschreibung && <p className="mt-1 text-xs text-muted-foreground">{n.beschreibung}</p>}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ 7. Quality Gate
const AMPEL = {
  gruen: { label: "Grün · Produktionsreif", cls: "bg-success/10 text-success border-success/40", dot: "bg-success" },
  hellgruen: { label: "Hellgrün · Freigabefähig", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/40", dot: "bg-emerald-500" },
  gelb: { label: "Gelb · Überarbeitung empfohlen", cls: "bg-warning/10 text-warning border-warning/40", dot: "bg-warning" },
  rot: { label: "Rot · Nicht freigabefähig", cls: "bg-danger/10 text-danger border-danger/40", dot: "bg-danger" },
};
const STATUS_TONE = { PASS: "green", PASS_WITH_WARNINGS: "amber", FAIL: "red" };
const STATUS_LABEL = { PASS: "PASS", PASS_WITH_WARNINGS: "PASS WITH WARNINGS", FAIL: "FAIL" };
const SEV = {
  error: { icon: XCircle, cls: "text-danger" },
  warning: { icon: WarningCircle, cls: "text-warning" },
  info: { icon: Info, cls: "text-primary" },
};

function Metric({ label, value, suffix }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-2xl font-black tabular-nums tracking-tight">{value}{suffix}</div>
      <div className="mt-0.5"><Label2>{label}</Label2></div>
    </div>
  );
}

function BucketBar({ label, score, weight, ko }) {
  const pct = weight ? Math.round((score / weight) * 100) : 0;
  return (
    <div data-testid={`qg-bucket-${label}`}>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium">{label} <span className="text-xs text-muted-foreground">(Gewicht {weight}%)</span></span>
        <span className="tabular-nums font-semibold">{score} / {weight}{ko ? <span className="ml-2 text-xs text-danger">K.-o.</span> : null}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted"><div className={`h-full rounded-full ${ko ? "bg-danger" : pct >= 90 ? "bg-success" : pct >= 70 ? "bg-warning" : "bg-danger"}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function QualityGateTab() {
  const [cats, setCats] = useState(null);
  const [sel, setSel] = useState("");
  const [qg, setQg] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/v1/cms/catalogs").then((r) => { setCats(r.data); if (r.data[0]) setSel(r.data[0].key); }); }, []);
  useEffect(() => {
    if (!sel) return;
    setLoading(true);
    api.get(`/v1/cms/quality-gate/${encodeURIComponent(sel)}`).then((r) => setQg(r.data)).finally(() => setLoading(false));
  }, [sel]);

  if (cats === null) return <Skeleton className="h-40 rounded-md" />;
  if (!cats.length) return <p className="text-sm text-muted-foreground">Kein Katalog vorhanden. Bitte zuerst ein CMS-Paket importieren.</p>;

  const qm = qg?.quality_metrics || {};
  const ov = qg?.overview || {};
  const cs = qg?.consistency || {};
  const amp = qg ? AMPEL[qg.ampel] : null;

  return (
    <div className="space-y-4" data-testid="cms-quality-gate">
      <div className="flex flex-wrap items-center gap-3">
        <Label2>Katalog</Label2>
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger className="w-72" data-testid="qg-catalog-select"><SelectValue /></SelectTrigger>
          <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.name} ({c.key} v{c.cms_version})</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading || !qg ? <Skeleton className="h-64 rounded-md" /> : (
        <>
          {/* Gesamtübersicht */}
          <Card className={`rounded-md border p-5 ${amp.cls}`} data-testid="qg-summary">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex h-20 w-20 flex-col items-center justify-center rounded-full border-4 border-current/30">
                  <span className="text-3xl font-black tabular-nums leading-none">{qg.score}</span>
                  <span className="text-[10px] uppercase opacity-70">Score</span>
                </div>
                <div>
                  <div className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${amp.dot}`} /><span className="font-heading text-lg font-black">{amp.label}</span></div>
                  <div className="mt-1 text-sm opacity-80">{qg.catalog.name} · {qg.catalog.key} v{qg.catalog.version}</div>
                </div>
              </div>
              <StatusBadge tone={STATUS_TONE[qg.status]} testid="qg-status">{STATUS_LABEL[qg.status]}</StatusBadge>
            </div>
            {qg.ko && <div className="mt-3 flex items-center gap-2 rounded-md bg-danger/10 px-3 py-2 text-sm text-danger"><XCircle size={16} weight="fill" /> Konsistenzfehler vorhanden — Katalog ist unabhängig vom Score nicht freigabefähig (hartes K.-o.).</div>}
          </Card>

          {/* Bewertungsbereiche */}
          <Card className="rounded-md border p-4" data-testid="qg-buckets">
            <div className="mb-3 flex items-center gap-2"><Gauge size={18} className="text-primary" /><span className="font-heading font-bold">Bewertung</span></div>
            <div className="space-y-3">
              <BucketBar label="Vollständigkeit" score={qg.buckets.vollstaendigkeit.score} weight={qg.buckets.vollstaendigkeit.weight} />
              <BucketBar label="Konsistenz" score={qg.buckets.konsistenz.score} weight={qg.buckets.konsistenz.weight} ko={qg.buckets.konsistenz.ko} />
              <BucketBar label="Wartungsqualität" score={qg.buckets.wartungsqualitaet.score} weight={qg.buckets.wartungsqualitaet.weight} />
              <BucketBar label="Wiederverwendbarkeit" score={qg.buckets.wiederverwendbarkeit.score} weight={qg.buckets.wiederverwendbarkeit.weight} />
            </div>
            {!qg.buckets.wiederverwendbarkeit.applicable && <p className="mt-2 text-xs text-muted-foreground">Wiederverwendbarkeit wird ab dem 2. Katalog messbar — der Score wird bis dahin über die übrigen Bereiche normalisiert.</p>}
            {/* Vollständigkeits-Checks */}
            <div className="mt-4 grid gap-1.5 sm:grid-cols-2">
              {qg.buckets.vollstaendigkeit.checks.map((c) => (
                <div key={c.label} className="flex items-center gap-2 text-sm" data-testid={`qg-check-${c.label}`}>
                  {c.ok ? <CheckCircle size={15} weight="fill" className="text-success" /> : <WarningCircle size={15} weight="fill" className="text-warning" />}
                  <span className="flex-1">{c.label}</span>
                  <span className="tabular-nums text-muted-foreground">{c.passed}/{c.total}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Vollständigkeit / Mengengerüst */}
          <Card className="rounded-md border p-4" data-testid="qg-overview">
            <div className="mb-3 flex items-center gap-2"><TreeStructure size={18} className="text-primary" /><span className="font-heading font-bold">Vollständigkeit · Mengengerüst</span></div>
            <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
              <Metric label="Prüfgruppen" value={ov.pruefgruppen} />
              <Metric label="Prüfpunkte" value={ov.pruefpunkte} />
              <Metric label="davon technisch" value={ov.technische_pruefpunkte} />
              <Metric label="davon organisatorisch" value={ov.organisatorische_pruefpunkte} />
              <Metric label="Komponenten" value={ov.komponenten} />
              <Metric label="Mängel" value={ov.maengel} />
              <Metric label="Reparaturpakete" value={ov.reparatur_pakete} />
              <Metric label="Business Rules" value={ov.business_rules} />
              <Metric label="Normverweise" value={ov.normen} />
              <Metric label="Health-Regeln" value={ov.health_rules} />
            </div>
          </Card>

          {/* Konsistenz */}
          <Card className="rounded-md border p-4" data-testid="qg-consistency">
            <div className="mb-3 flex items-center gap-2"><GitBranch size={18} className="text-primary" /><span className="font-heading font-bold">Konsistenz</span></div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ["Fehlende Referenzen", cs.missing_references?.length || 0, true],
                ["Verwaiste Objekte", Object.values(cs.orphaned || {}).reduce((n, l) => n + l.length, 0), true],
                ["Duplikate (IDs)", Object.values(cs.duplicate_keys || {}).reduce((n, l) => n + l.length, 0) + (cs.duplicate_checkpoints?.length || 0), true],
                ["Versionskonflikte", cs.version_conflicts?.length || 0, true],
                ["Kreisreferenzen (nur Hinweis)", cs.circular_references?.length || 0, false],
              ].map(([label, count, hard]) => (
                <div key={label} className="flex items-center gap-2 rounded-md border p-3 text-sm" data-testid={`qg-cons-${label}`}>
                  {count === 0 ? <CheckCircle size={16} weight="fill" className="text-success" /> : hard ? <XCircle size={16} weight="fill" className="text-danger" /> : <Info size={16} weight="fill" className="text-muted-foreground" />}
                  <span className="flex-1">{label}</span>
                  <Badge variant="outline" className="rounded-md tabular-nums">{count}</Badge>
                </div>
              ))}
            </div>
          </Card>

          {/* Wartungsqualität */}
          <Card className="rounded-md border p-4" data-testid="qg-quality">
            <div className="mb-3 flex items-center gap-2"><ShieldWarning size={18} className="text-primary" /><span className="font-heading font-bold">Wartungsqualität</span></div>
            <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-3">
              <Metric label="Anteil sicherheitsrelevant" value={qm.anteil_sicherheit} suffix="%" />
              <Metric label="Anteil Pflichtfotos" value={qm.anteil_pflichtfoto} suffix="%" />
              <Metric label="Anteil Messungen" value={qm.anteil_messung} suffix="%" />
              <Metric label="Anteil Business Rules" value={qm.anteil_business_rule} suffix="%" />
              <Metric label="Anteil Kommentarpflicht" value={qm.anteil_kommentarpflicht} suffix="%" />
              <Metric label="Anteil Folgeprüfungen" value={qm.anteil_folgepruefung} suffix="%" />
              <Metric label="Ø Health-Gewichtung" value={qm.avg_health} />
              <Metric label="Ø Mängel / Prüfpunkt" value={qm.avg_maengel_pro_pruefpunkt} />
              <Metric label="Ø Reparatur / Prüfpunkt" value={qm.avg_reparatur_pro_pruefpunkt} />
            </div>
          </Card>

          {/* Wiederverwendbarkeit */}
          <Card className="rounded-md border p-4" data-testid="qg-reuse">
            <div className="mb-3 flex items-center gap-2"><Recycle size={18} className="text-primary" /><span className="font-heading font-bold">Wiederverwendbarkeit (katalogübergreifend)</span></div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {["komponenten", "maengel", "reparatur_pakete", "business_rules"].map((lib) => {
                const r = qg.reusability[lib] || { reused: 0, total: 0 };
                return <Metric key={lib} label={`${LIB_LABEL[lib]} wiederverwendet`} value={`${r.reused}/${r.total}`} />;
              })}
            </div>
          </Card>

          {/* Empfehlungen */}
          <Card className="rounded-md border p-4" data-testid="qg-recommendations">
            <div className="mb-3 flex items-center gap-2"><Lightbulb size={18} className="text-primary" /><span className="font-heading font-bold">Verbesserungsvorschläge ({qg.recommendations.length})</span></div>
            {qg.recommendations.length === 0 ? <p className="text-sm text-muted-foreground">Keine Auffälligkeiten — der Katalog erfüllt alle Prüfkriterien.</p> : (
              <div className="space-y-2">
                {qg.recommendations.map((r, i) => {
                  const S = SEV[r.severity] || SEV.info;
                  return (
                    <div key={i} className="flex items-start gap-2 rounded-md border border-border/60 p-3 text-sm" data-testid="qg-rec-row">
                      <S.icon size={16} weight="fill" className={`mt-0.5 shrink-0 ${S.cls}`} />
                      <div>
                        <div className="font-medium">{r.message}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">→ {r.empfehlung}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Page
export default function CmsAbnahme() {
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);
  return (
    <div>
      <PageHeader title="CMS-Engine · Abnahme & Diagnose" subtitle="Read-only Verifikationsoberfläche der datengetriebenen CMS-Engine (Phase 1) · kein Editor" />
      <Tabs defaultValue="diagnose">
        <TabsList data-testid="cms-tabs" className="flex-wrap">
          <TabsTrigger value="diagnose" data-testid="tab-diagnose"><ChartBar size={15} weight="duotone" className="mr-1.5" /> Architekturdiagnose</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import"><UploadSimple size={15} weight="duotone" className="mr-1.5" /> Paketimport</TabsTrigger>
          <TabsTrigger value="inspektion" data-testid="tab-inspektion"><TreeStructure size={15} weight="duotone" className="mr-1.5" /> Katalog-Inspektion</TabsTrigger>
          <TabsTrigger value="core" data-testid="tab-core"><Graph size={15} weight="duotone" className="mr-1.5" /> CMS-Core</TabsTrigger>
          <TabsTrigger value="referenz" data-testid="tab-referenz"><GitBranch size={15} weight="duotone" className="mr-1.5" /> Referenzprüfung</TabsTrigger>
          <TabsTrigger value="qualitygate" data-testid="tab-qualitygate"><SealCheck size={15} weight="duotone" className="mr-1.5" /> Quality Gate</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ClockCounterClockwise size={15} weight="duotone" className="mr-1.5" /> Audit</TabsTrigger>
        </TabsList>
        <TabsContent value="diagnose" className="mt-4"><DiagnoseTab key={`d${refreshKey}`} /></TabsContent>
        <TabsContent value="import" className="mt-4"><ImportTab onImported={bump} /></TabsContent>
        <TabsContent value="inspektion" className="mt-4"><InspektionTab key={`i${refreshKey}`} /></TabsContent>
        <TabsContent value="core" className="mt-4"><CoreTab key={`c${refreshKey}`} /></TabsContent>
        <TabsContent value="referenz" className="mt-4"><ReferenzTab key={`r${refreshKey}`} /></TabsContent>
        <TabsContent value="qualitygate" className="mt-4"><QualityGateTab key={`q${refreshKey}`} /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditTab key={`a${refreshKey}`} /></TabsContent>
      </Tabs>
    </div>
  );
}
